// src/pages/LookupTestPage.js
import React, { useState } from "react";
import LookupUmat from "../components/LookupUmat";
import LookupAgendaMaster from "../components/LookupAgendaMaster";
import LookupExternalPerson from "../components/LookupExternalPerson";
import { getJwt } from "../utils/auth"; // sesuaikan path bila helper ada di lokasi lain

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LookupTestPage() {
  const [singleUmat, setSingleUmat] = useState(null);
  const [multiUmat, setMultiUmat] = useState([]);
  const [agendaMaster, setAgendaMaster] = useState(null);
  const [externalPeople, setExternalPeople] = useState([]);

  const [status, setStatus] = useState(null);
  const [recordId, setRecordId] = useState(""); // id untuk retrieve/update contoh

  function headers() {
    const h = { "Content-Type": "application/json" };
    try {
      const jwt = getJwt?.();
      if (jwt) h["Authorization"] = `Bearer ${jwt}`;
    } catch (e) {
      // ignore if getJwt not available
    }
    return h;
  }

  // contoh save ke endpoint Strapi. Sesuaikan endpoint & field names dengan schema Anda.
  async function handleSaveCreate() {
    setStatus("saving...");
    try {
      const payload = {
        data: {
          subject: "Test from UI",
          agenda_master: agendaMaster?.id ?? null,
          // adjust field names according to your schema
          umats: multiUmat.map(u => u.id),
          external_people: externalPeople.map(e => e.id),
        }
      };
      const url = `${API_URL}/api/selection-tests`; // GANTI sesuai content-type nyata Anda
      const resp = await fetch(url, { method: "POST", headers: headers(), body: JSON.stringify(payload) });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error?.message ?? `HTTP ${resp.status}`);
      setStatus({ ok: true, data });
      setRecordId(String(data?.data?.id ?? ""));
    } catch (err) {
      setStatus({ ok: false, error: String(err?.message ?? err) });
    }
  }

  // retrieve existing record and populate selected state
  async function handleRetrieve() {
    if (!recordId) { setStatus("no id"); return; }
    setStatus("retrieving...");
    try {
      const url = `${API_URL}/api/selection-tests/${encodeURIComponent(recordId)}?populate=*&_publicationState=preview`;
      const resp = await fetch(url);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error?.message ?? `HTTP ${resp.status}`);

      const attrs = data?.data?.attributes ?? data?.attributes ?? {};
      const normalize = arr => (Array.isArray(arr) ? arr.map(d => (d?.attributes ? { id: d.id, ...d.attributes } : d)) : []);
      // populate multi relations
      setMultiUmat(normalize(attrs.umats ?? attrs.umats?.data ?? []));
      setExternalPeople(normalize(attrs.external_people ?? attrs.external_people?.data ?? []));
      // populate single relation agenda_master (handle possible shapes)
      if (attrs.agenda_master?.data) {
        setAgendaMaster({ id: attrs.agenda_master.data.id, ...(attrs.agenda_master.data.attributes ?? {}) });
      } else if (attrs.agenda_master?.id) {
        setAgendaMaster({ id: attrs.agenda_master.id, ...(attrs.agenda_master.attributes ?? {}) });
      } else {
        setAgendaMaster(null);
      }

      setStatus({ ok: true, data });
    } catch (err) {
      setStatus({ ok: false, error: String(err?.message ?? err) });
    }
  }

  // update record by id
  async function handleUpdate() {
    if (!recordId) { setStatus("no id"); return; }
    setStatus("updating...");
    try {
      const payload = {
        data: {
          agenda_master: agendaMaster?.id ?? null,
          umats: multiUmat.map(u => u.id),
          external_people: externalPeople.map(e => e.id),
        }
      };
      const url = `${API_URL}/api/selection-tests/${encodeURIComponent(recordId)}`;
      const resp = await fetch(url, { method: "PUT", headers: headers(), body: JSON.stringify(payload) });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error?.message ?? `HTTP ${resp.status}`);
      setStatus({ ok: true, data });
    } catch (err) {
      setStatus({ ok: false, error: String(err?.message ?? err) });
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <h2 className="text-xl font-semibold">Lookup Test Page</h2>

      <div>
        <h4>Single select — Umat</h4>
        <LookupUmat onChange={setSingleUmat} multiple={false} />
        <div className="mt-2 text-xs text-gray-600">Raw value (singleUmat):</div>
        <pre className="bg-white p-3 border">{JSON.stringify(singleUmat, null, 2)}</pre>
      </div>

      <div>
        <h4>Multiple select — Umat</h4>
        <LookupUmat onChange={setMultiUmat} multiple={true} />
        <div className="mt-2 text-xs text-gray-600">Raw value (multiUmat):</div>
        <pre className="bg-white p-3 border">{JSON.stringify(multiUmat, null, 2)}</pre>
      </div>

      <div>
        <h4>Single select — AgendaMaster</h4>
        <LookupAgendaMaster onChange={setAgendaMaster} multiple={false} />
        <div className="mt-2 text-xs text-gray-600">Raw value (agendaMaster):</div>
        <pre className="bg-white p-3 border">{JSON.stringify(agendaMaster, null, 2)}</pre>
      </div>

      <div>
        <h4>Multiple select — External Person</h4>
        <LookupExternalPerson onChange={setExternalPeople} multiple={true} />
        <div className="mt-2 text-xs text-gray-600">Raw value (externalPeople):</div>
        <pre className="bg-white p-3 border">{JSON.stringify(externalPeople, null, 2)}</pre>
      </div>

      <div className="flex gap-3 mt-4">
        <button className="px-4 py-2 bg-blue-600 text-white rounded" onClick={handleSaveCreate}>Save (create)</button>
        <input
          className="border px-2"
          placeholder="record id to retrieve/update"
          value={recordId}
          onChange={e => setRecordId(e.target.value)}
        />
        <button className="px-3 py-2 border rounded" onClick={handleRetrieve}>Retrieve</button>
        <button className="px-3 py-2 border rounded" onClick={handleUpdate}>Update</button>
      </div>

      <div className="mt-3">
        <div className="text-sm">Status:</div>
        <pre className="bg-gray-50 p-3 border">{JSON.stringify(status, null, 2)}</pre>
      </div>

      <div className="text-xs text-gray-500">
        <strong>Note:</strong> Endpoint <code>/api/selection-tests</code> di atas hanyalah contoh. Jika ingin menyimpan ke content-type <code>agendas</code> ganti endpoint menjadi <code>/api/agendas</code> dan sesuaikan nama field relasi pada payload (mis. <code>{'{ data: { agenda_master: id, umats: [ids], external_people: [ids] } }'}</code>). Pastikan nama field relasi sesuai schema Strapi Anda.
      </div>
    </div>
  );
}
